// What an artifact costs to place, and who pays (§31 A3–A5).
//
// The design's universal rule: "Artifact costs are first pulled from the
// inventory of the equipped Monarch when applicable, then from the stockpiles
// closest to where the special structure is placed." The equipped Monarch is
// the one `syncGear` will issue the gear to — the owner's nearest Monarch —
// so the payer and the bearer are one answer (`nearestMonarch`). Its inbox
// first, cheapest first; then the owner's stores nearest the site, S3's order.
//
// The bill is `STRUCTURE_ROWS[kind].bill`, except the Titan Relic's, which
// climbs by `RULES.relicStep` marble for every Relic the side has placed
// before, held or lost (`run.relicsBuilt`). Every artifact pays (§31 A7
// priced the Keystone, the last token-only row). One function answers the
// reducer and the board's highlight, so the two cannot disagree.

import type { Side } from "../board";
import type { GameState, RunState } from "../commands";
import {
  ARTISAN_PILLAR,
  bufferAtLeast,
  LEGION_TOWER,
  PROPAGANDA_TOWER,
  RELIC,
  type StructureKind, structureAt, takeUnitOf, unitMeets } from "../industry/state";
import { storesNearest, takeStockOf } from "../industry/structures";
import { type ForgeRow, type Grade, RULES, structureRow } from "../rules";
import { isLadder, STONE } from "../materials";
import { nearestMonarch } from "./equipment";
import { artisanPlaceError } from "./artisan";
import { stokePlaceError } from "./stoke";
import { legionPlaceError } from "./legion";

/**
 * Why `kind` may not be placed by `side` for its own uniqueness rule, or null:
 * the Pillar of Excellence (§31 A6, one on the map, none once lost), Stoke
 * the Forges (A10, one per side). The reducer and the placing chip both ask.
 */
export function uniquePlaceError(state: GameState, kind: StructureKind, side: Side): string | null {
  if (kind === ARTISAN_PILLAR) return artisanPlaceError(state.position, state.industry);
  if (kind === PROPAGANDA_TOWER) return stokePlaceError(state.industry, side);
  if (kind === LEGION_TOWER) return legionPlaceError(state.industry, side);
  return null;
}

/** Relics `side` has placed this run, held or lost (§31 A5). */
export const relicsBuiltBy = (run: RunState | undefined, side: Side): number =>
  run?.relicsBuilt?.[side] ?? 0;

/** What placing `kind` costs `side` now. */
export function artifactBill(state: GameState, kind: StructureKind, side: Side): ForgeRow[] {
  const row = structureRow(kind);
  if (kind === RELIC) {
    const count = row.bill[0].count + RULES.relicStep * relicsBuiltBy(state.run, side);
    return [{ line: STONE, grade: row.bill[0].grade, count }];
  }
  return row.bill.map((one) => ({ ...one }));
}

/** How much of one row `side` can pay for a placement at `at`. */
function funds(state: GameState, side: Side, row: ForgeRow, at: number): number {
  const { industry, position } = state;
  const monarch = nearestMonarch(position, side, at);
  let total = monarch < 0 ? 0 : bufferAtLeast(industry, monarch, row.grade as Grade, "in", row.line as 0);
  for (const sq of storesNearest(industry, side, at)) {
    for (const unit of structureAt(industry, sq)!.stock) if (unitMeets(unit, row.line as 0, row.grade)) total++;
  }
  return total;
}

/**
 * Can `side` pay for `kind` placed at `at`? **A ladder row's units also meet
 * every lower row of its line** (marble pays stone), so each row is tested
 * against the whole bill at or above its rung — the Fusion Chamber's 15 stone
 * + 5 marble is the first artifact where one unit could count twice (§31 A8).
 */
export function canPayArtifact(state: GameState, kind: StructureKind, side: Side, at: number): boolean {
  const bill = artifactBill(state, kind, side);
  return bill.every((row) => {
    const need = bill
      .filter((other) => other.line === row.line && (isLadder(row.line) ? other.grade >= row.grade : other.grade === row.grade))
      .reduce((sum, other) => sum + other.count, 0);
    return funds(state, side, row, at) >= need;
  });
}

/**
 * Pay for `kind` at `at` out of `next`, mutating it. The caller has asked
 * `canPayArtifact` of the same state. Counts a Relic toward the climb.
 */
export function payArtifact(next: GameState, kind: StructureKind, side: Side, at: number): void {
  const { industry, position } = next;
  const monarch = nearestMonarch(position, side, at);
  // Dearest rung first, so a cheap row spending up the ladder never eats a
  // unit a dearer row needs.
  const bill = [...artifactBill(next, kind, side)].sort((a, b) => a.line - b.line || b.grade - a.grade);
  for (const row of bill) {
    for (let i = 0; i < row.count; i++) {
      if (monarch >= 0 && takeUnitOf(industry, monarch, row.line as 0, row.grade) >= 0) continue;
      for (const sq of storesNearest(industry, side, at)) {
        if (takeStockOf(structureAt(industry, sq)!, row.line, row.grade)) break;
      }
    }
  }
  if (kind === RELIC && next.run) {
    const built = [...(next.run.relicsBuilt ?? [])];
    while (built.length <= side) built.push(0);
    built[side]++;
    next.run = { ...next.run, relicsBuilt: built };
  }
}
